import { useEffect, useState } from 'react';
import type { ChatDayInvitationState } from '@chat-emp/shared';

import type { CtaPayload } from '../fixtures/domain.js';
import { fetchChatDayInvitations } from '../lib/facade.js';

/** The BFF route Jobie's Accept / Decline buttons call (chat-marketing-dashboard invite.ts). */
const RESPOND_ROUTE = '/api/chat-day/respond';
const INVITE_CODE = /^[0-9a-f]{32}$/;
/** The BFF's own cap on one ask. */
const BATCH = 50;

/** The invitation a Chat Day card answers, or undefined for any other row. */
export function chatDayInviteCodeOf(cta: CtaPayload | null): string | undefined {
  for (const button of cta?.buttons ?? []) {
    const code = button.payload?.invite_code;
    if (button.url === RESPOND_ROUTE && typeof code === 'string' && INVITE_CODE.test(code)) {
      return code;
    }
  }
  return undefined;
}

/**
 * Known states, by invite code. A code asked and not answered is null: chat-kit
 * does not know it for this company, or the ask failed, and either way the card
 * stays as the message says, since chat-kit still refuses a tap it cannot take.
 */
const known = new Map<string, ChatDayInvitationState | null>();
const waiting = new Set<string>();
const listeners = new Set<() => void>();
let queued = false;

/**
 * One ask for every card a thread mounts in the same tick, rather than one per
 * card: a thread opens with all of Jobie's invitations rendering at once.
 */
function ask(code: string): void {
  if (known.has(code) || waiting.has(code)) return;
  waiting.add(code);
  if (queued) return;
  queued = true;
  queueMicrotask(() => {
    queued = false;
    const codes = [...waiting];
    waiting.clear();
    for (let i = 0; i < codes.length; i += BATCH) {
      const batch = codes.slice(i, i + BATCH);
      void fetchChatDayInvitations(batch)
        .then(({ invitations }) => batch.forEach((c) => known.set(c, invitations[c] ?? null)))
        .catch(() => batch.forEach((c) => known.set(c, null)))
        .finally(() => listeners.forEach((notify) => notify()));
    }
  });
}

/**
 * Where a Chat Day card's invitation stands, read once per page load: what
 * keeps a card chat-kit closed (link generated, day over, already answered)
 * closed after a reload, when the message itself was never sealed.
 */
export function useChatDayInvitation(code: string | undefined): ChatDayInvitationState | null {
  const [state, setState] = useState(() => (code ? (known.get(code) ?? null) : null));

  useEffect(() => {
    if (!code) {
      setState(null);
      return;
    }
    const sync = () => setState(known.get(code) ?? null);
    listeners.add(sync);
    sync();
    ask(code);
    return () => {
      listeners.delete(sync);
    };
  }, [code]);

  return state;
}

export function resetChatDayInvitationsForTest(): void {
  known.clear();
  waiting.clear();
  listeners.clear();
  queued = false;
}
